import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up the worker for react-pdf with version matching
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MonthlyPDFViewer = ({ testMonth = null }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageRef = useRef(null);

  // Map months to PDF pages (September is page 1, October is page 2, ..., June is page 10)
  const monthToPageMap = {
    8: 1,   // September
    9: 2,   // October
    10: 3,  // November
    11: 4,  // December
    0: 5,   // January
    1: 6,   // February
    2: 7,   // March
    3: 8,   // April
    4: 9,   // May
    5: 10   // June
  };

  const monthNames = {
    8: 'September',
    9: 'October',
    10: 'November',
    11: 'December',
    0: 'January',
    1: 'February',
    2: 'March',
    3: 'April',
    4: 'May',
    5: 'June'
  };

  useEffect(() => {
    // Use test month if provided, otherwise use current month
    const currentMonth = testMonth !== null ? testMonth : new Date().getMonth();
    let pageNumber = monthToPageMap[currentMonth];
    
    // For July and August (summer months), show September's page (page 1)
    if (currentMonth === 6 || currentMonth === 7) { // July or August
      pageNumber = 1; // Show September's page
    }
    
    if (pageNumber) {
      setCurrentPage(pageNumber);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testMonth]);

  const onDocumentLoadSuccess = () => {
    setIsLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load PDF document');
    setIsLoading(false);
  };

  const getCurrentMonthName = () => {
    const currentMonth = testMonth !== null ? testMonth : new Date().getMonth();
    // For July and August, show "September (Summer Preview)"
    if (currentMonth === 6 || currentMonth === 7) {
      return 'September (Summer Preview)';
    }
    return monthNames[currentMonth] || 'September';
  };

  // pdf.js renders each PDF link annotation as an empty <a>: the annotation
  // carries a destination but no text, and the artwork it sits on lives in the
  // canvas, so every one of these links reaches the DOM with no accessible
  // name. Name them here, numbering by destination so that the several
  // annotation boxes that share one video read as a single link.
  const nameAnnotationLinks = useCallback(() => {
    const links = pageRef.current?.querySelectorAll('.react-pdf__Page__annotations a[href]');
    if (!links?.length) return;

    const byDestination = new Map();
    links.forEach((link) => {
      const group = byDestination.get(link.href) || [];
      group.push(link);
      byDestination.set(link.href, group);
    });

    const month = getCurrentMonthName();
    const total = byDestination.size;
    let index = 0;
    byDestination.forEach((group) => {
      index += 1;
      const kind = /(?:youtube\.com|youtu\.be)/.test(group[0].href) ? 'Video' : 'Link';
      const label = `${kind} ${index} of ${total} in the ${month} digital library page (opens in a new tab)`;
      group.forEach((link) => {
        if (!link.textContent.trim()) link.setAttribute('aria-label', label);
      });
    });
  // getCurrentMonthName reads the same input this depends on
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testMonth]);

  // react-pdf fires onRenderAnnotationLayerSuccess before pdf.js has actually
  // appended the anchors, so watch the layer instead. Every pass relabels the
  // whole set, which keeps the "n of total" numbering consistent no matter how
  // many mutations it takes for the layer to settle. Attribute changes are not
  // observed, so writing the labels cannot retrigger this.
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return undefined;
    nameAnnotationLinks();
    const observer = new MutationObserver(nameAnnotationLinks);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [nameAnnotationLinks]);

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="p-8 text-center">
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">
            {getCurrentMonthName()} Book & SEL Word of the Month
          </h3>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="text-4xl mb-4">⚠️</div>
            <p className="text-lg text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="p-8">
        <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
          {getCurrentMonthName()} Book & SEL Word of the Month
        </h3>
        
        {isLoading && (
          <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#630503] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading PDF...</p>
            </div>
          </div>
        )}

        <div className="flex justify-center" ref={pageRef}>
          <Document
            file="/assets/documents/library/2025-2026 DIGITAL SEL DIGITAL LIBRARY.pdf"
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
            className="max-w-full"
          >
            {currentPage && !isLoading && (
              <Page
                pageNumber={currentPage}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                width={Math.min(1000, window.innerWidth - 80)}
                className="shadow-lg rounded-lg overflow-hidden"
              />
            )}
          </Document>
        </div>

        {!isLoading && currentPage && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Click on the links in this page to explore!!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyPDFViewer;
